/**
 * Twilio Voice Handler
 *
 * Wraps the @twilio/voice-sdk Device class and manages WebRTC voice connections.
 * Based on: https://www.twilio.com/docs/voice/sdks/javascript/get-started
 *
 * Device lifecycle:
 *   1. new Device(token, options)
 *   2. device.register()  — opens signaling WebSocket for incoming calls
 *   3. device.connect()   — initiates an outbound call
 *   4. device.destroy()   — tears everything down
 *
 * Call lifecycle:
 *   - Outbound: device.connect() → Call (accept → ringing → open → disconnect)
 *   - Inbound:  device 'incoming' event → call.accept() → (open → disconnect)
 */

import { Device, Call } from '@twilio/voice-sdk';
import type { VoiceEvent, VoiceEventType } from '@/types/voice';

export type VoiceEventCallback = (event: VoiceEvent) => void;

export class TwilioVoiceHandler {
  private device: Device | null = null;
  private connections: Map<string, Call> = new Map();
  private onEvent: VoiceEventCallback;

  // Track recording state per call (first start vs pause/resume)
  private recordingStartedCalls: Set<string> = new Set();

  constructor(onEvent: VoiceEventCallback) {
    this.onEvent = onEvent;
  }

  // ── Device Lifecycle ──────────────────────────────────────────────

  /**
   * Initialize (or re-initialize) the Twilio Device with an access token.
   * Registers the device for incoming calls.
   *
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice
   */
  async initialize(token: string): Promise<boolean> {
    try {
      // Destroy existing device if any
      if (this.device) {
        this.device.destroy();
        this.device = null;
      }

      // Create new Device
      // @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#new-devicetoken-options
      this.device = new Device(token, {
        allowIncomingWhileBusy: true,
        closeProtection: true,
        logLevel: 'warn',
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      // Attach device events
      this.attachDeviceListeners();

      // Register the device to receive incoming calls
      // @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceregister
      await this.device.register();

      return true;
    } catch (error) {
      console.error('[VoiceHandler] Failed to initialize device:', error);
      this.emit('device-error', {
        errorMessage:
          error instanceof Error ? error.message : 'Failed to initialize',
      });
      return false;
    }
  }

  /**
   * Destroy the device and clean up all connections.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#devicedestroy
   */
  destroy(): void {
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    this.connections.clear();
    this.recordingStartedCalls.clear();
  }

  /**
   * Update the access token (e.g. before it expires).
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceupdatetokentoken
   */
  updateToken(token: string): void {
    if (this.device) {
      this.device.updateToken(token);
    }
  }

  /** Get current device state */
  getDeviceState(): string {
    return this.device?.state ?? 'destroyed';
  }

  /** Whether the device is registered and ready */
  isReady(): boolean {
    return this.device?.state === Device.State.Registered;
  }

  // ── Call Operations ───────────────────────────────────────────────

  /**
   * Initiate an outbound call.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceconnectconnectoptions
   *
   * ConnectOptions.params are sent to the TwiML app's VoiceUrl as request parameters.
   */
  async makeCall(
    callerId: string,
    recipientNumber: string,
    uiCallId: string,
    adminUserId: string,
    isWhatsAppCall: boolean = false
  ): Promise<void> {
    if (!this.device) {
      throw new Error('Device not initialized');
    }

    // Clean the recipient number
    const cleanNumber = recipientNumber.replace(/[^0-9+]/g, '');

    // Connect with custom parameters that get sent to the TwiML app's VoiceUrl
    // Params match the old static code exactly (twilio-call-things.js line 892-901)
    const call = await this.device.connect({
      params: {
        clientCallerId: callerId,
        adminUserId,
        isCallFromClient: 'true',
        recipientNumber: cleanNumber,
        // WhatsApp calling: tell the TwiML handler to use <Dial><WhatsApp> routing
        ...(isWhatsAppCall && {
          isWhatsAppCall: 'true',
          whatsAppCallerId: callerId.startsWith('whatsapp:') ? callerId : `whatsapp:${callerId}`,
          whatsAppRecipient: cleanNumber.startsWith('whatsapp:') ? cleanNumber : `whatsapp:${cleanNumber}`,
        }),
      },
    });

    // Store the connection with the UI call ID initially
    this.connections.set(uiCallId, call);

    // Attach call event listeners
    this.attachCallListeners(call, uiCallId);

    // Emit connecting status
    this.emit('call-connecting', {
      callId: uiCallId,
      customerNumber: cleanNumber,
      companyCallerId: callerId,
      direction: 'outbound',
      callChannel: isWhatsAppCall ? 'whatsapp' : 'pstn',
    });
  }

  /**
   * Accept an incoming call.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callacceptacceptoptions
   */
  acceptCall(callId: string): void {
    const call = this.connections.get(callId);
    if (call) {
      call.accept();
    }
  }

  /**
   * End/disconnect a call. Uses reject() for pending, disconnect() for active.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#calldisconnect
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callreject
   */
  endCall(callId: string): boolean {
    const call = this.connections.get(callId);
    // Report whether anything was actually sent to Twilio. Returning silently
    // here used to let the UI mark a call ended while its line stayed up.
    if (!call) {
      console.warn('[VoiceHandler] endCall: no browser connection for', callId);
      return false;
    }

    const status = call.status();
    if (status === 'pending') {
      call.reject();
      return true;
    }
    if (
      status === 'open' ||
      status === 'connecting' ||
      status === 'ringing' ||
      status === 'reconnecting'
    ) {
      call.disconnect();
      return true;
    }
    console.warn('[VoiceHandler] endCall: connection for', callId, 'is', status);
    return false;
  }

  /**
   * Ignore an incoming call without alerting the caller.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callignore
   */
  ignoreCall(callId: string): void {
    const call = this.connections.get(callId);
    if (call) {
      call.ignore();
      this.emit('call-canceled', { callId });
    }
  }

  /**
   * Mute or unmute the local audio input.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callmuteshouldmute
   */
  muteCall(callId: string, shouldMute: boolean): void {
    const call = this.connections.get(callId);
    if (call) {
      call.mute(shouldMute);
    }
  }

  /**
   * Send DTMF digits.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callsenddigitsdigits
   */
  sendDigits(callId: string, digits: string): void {
    const call = this.connections.get(callId);
    if (call) {
      call.sendDigits(digits);
    }
  }

  /** Check if a call is currently active (open or ringing) */
  isCallActive(callId: string): boolean {
    const call = this.connections.get(callId);
    if (!call) return false;
    const status = call.status();
    return status === 'open' || status === 'ringing' || status === 'reconnecting';
  }

  /** Whether this call has already started recording */
  hasRecordingStarted(callId: string): boolean {
    return this.recordingStartedCalls.has(callId);
  }

  /** Mark that a call has started recording */
  markRecordingStarted(callId: string): void {
    this.recordingStartedCalls.add(callId);
  }

  // ── Private: Device Event Listeners ───────────────────────────────

  private attachDeviceListeners(): void {
    if (!this.device) return;

    /**
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#registered-event
     */
    this.device.on('registered', () => {
      console.log('[VoiceHandler] Device registered');
      this.emit('device-registered');
    });

    this.device.on('registering', () => {
      this.emit('device-registering');
    });

    this.device.on('unregistered', () => {
      console.log('[VoiceHandler] Device unregistered');
      this.emit('device-unregistered');
    });

    /**
     * Incoming call event — fires when a new inbound call is received.
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#incoming-event
     */
    this.device.on('incoming', (call: Call) => {
      this.handleIncomingCall(call);
    });

    /**
     * Device error event.
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#error-event
     */
    this.device.on('error', (error) => {
      console.error('[VoiceHandler] Device error:', error);
      this.emit('device-error', {
        errorMessage: error.message || 'Device error',
      });
    });

    /**
     * Token expiring soon — fetch a new token and call device.updateToken().
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#tokenwillexpire-event
     */
    this.device.on('tokenWillExpire', () => {
      console.log('[VoiceHandler] Token will expire soon');
      this.emit('token-will-expire');
    });

    this.device.on('destroyed', () => {
      this.emit('device-destroyed');
    });
  }

  // ── Private: Call Event Listeners ─────────────────────────────────

  /**
   * Attach event listeners to a Call object.
   * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall
   */
  private attachCallListeners(call: Call, uiCallId: string): void {
    /**
     * Fired when the call is accepted (media session begins).
     * For outbound calls, this fires when the connection is established.
     * For inbound calls, this fires after call.accept().
     */
    call.on('accept', (acceptedCall: Call) => {
      const callSid = acceptedCall.parameters?.CallSid;

      // Update the connection map if CallSid differs from uiCallId
      if (callSid && callSid !== uiCallId) {
        this.connections.set(callSid, acceptedCall);
        if (this.connections.has(uiCallId)) {
          this.connections.delete(uiCallId);
        }
        this.emit('call-id-updated', {
          oldCallId: uiCallId,
          newCallId: callSid,
        });
        uiCallId = callSid;
      }

      this.emit('call-accepted', { callId: uiCallId });
    });

    /**
     * Fired when the call starts ringing on the remote end.
     */
    call.on('ringing', () => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit('call-ringing', { callId: callSid });
    });

    /**
     * Fired when the call is disconnected (ended normally).
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#disconnect-event
     */
    call.on('disconnect', () => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit('call-disconnected', { callId: callSid });
      this.connections.delete(callSid);
      this.connections.delete(uiCallId);
      this.recordingStartedCalls.delete(callSid);
    });

    /**
     * Fired when the call is canceled (caller hangs up before answer).
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#cancel-event
     */
    call.on('cancel', () => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit('call-canceled', { callId: callSid });
      this.connections.delete(callSid);
      this.connections.delete(uiCallId);
    });

    /**
     * Fired when the call is rejected.
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#reject-event
     */
    call.on('reject', () => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit('call-rejected', { callId: callSid });
      this.connections.delete(callSid);
      this.connections.delete(uiCallId);
    });

    /**
     * Fired when mute state changes.
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#mute-event-1
     */
    call.on('mute', (isMuted: boolean) => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit(isMuted ? 'call-muted' : 'call-unmuted', {
        callId: callSid,
      });
    });

    /**
     * Call error event.
     * @see https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#error-event-1
     */
    call.on('error', (error) => {
      const callSid = call.parameters?.CallSid || uiCallId;
      this.emit('call-error', {
        callId: callSid,
        errorMessage: error.message || 'Call error',
      });
    });
  }

  // ── Private: Incoming Call ────────────────────────────────────────

  private handleIncomingCall(call: Call): void {
    const callSid = call.parameters?.CallSid;
    const fromNumber = call.parameters?.From || '';
    const companyCallerId =
      call.customParameters?.get('companyCallerID') || call.parameters?.To || '';

    if (!callSid) {
      console.warn('[VoiceHandler] Incoming call has no CallSid');
      return;
    }

    // Detect WhatsApp calls — From/To use whatsapp:+XXX format
    const isWhatsAppCall = fromNumber.startsWith('whatsapp:') || companyCallerId.startsWith('whatsapp:');

    // Store the connection
    this.connections.set(callSid, call);

    // Attach listeners
    this.attachCallListeners(call, callSid);

    // Notify the store
    this.emit('incoming-call', {
      callId: callSid,
      customerNumber: fromNumber,
      companyCallerId,
      direction: 'inbound',
      callChannel: isWhatsAppCall ? 'whatsapp' : 'pstn',
    });
  }

  // ── Private: Event Emitter Helper ─────────────────────────────────

  private emit(
    type: VoiceEventType,
    data?: Omit<VoiceEvent, 'type'>
  ): void {
    this.onEvent({ type, ...data });
  }
}
